import { useEffect, useState } from 'react';
import {
  platformBillingApi,
  type TenantSubscription, type PublicPlan, type PlatformInvoice, type UsageSnapshot,
} from '../../api';
import { humanise, humaniseStatus } from '../../lib/labels';

// Tenant-facing Billing: current plan / usage / issued platform invoices
// plus a simple change-plan flow. Payment itself rides on the same PayFast
// link flow we use for chambers invoices - no separate gateway here.

const ZAR = (n: number | null | undefined) =>
  n == null ? '-' : new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(n);

const asBool = (v: unknown) => v === true || v === 'true';

export default function SettingsBilling() {
  const [sub, setSub] = useState<TenantSubscription | null>(null);
  const [plan, setPlan] = useState<PublicPlan | null>(null);
  const [usage, setUsage] = useState<UsageSnapshot[]>([]);
  const [limits, setLimits] = useState<{ metric_key: string; limit_value: number; soft_limit_value: number }[]>([]);
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [invoices, setInvoices] = useState<PlatformInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [s, p, i] = await Promise.all([
        platformBillingApi.subscription(),
        platformBillingApi.publicPlans().catch(() => ({ plans: [] })),
        platformBillingApi.invoices().catch(() => ({ invoices: [] })),
      ]);
      setSub(s.subscription);
      setPlan(s.plan);
      setUsage(s.usage);
      setLimits(s.limits);
      setPlans(p.plans);
      setInvoices(i.invoices);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const changePlan = async (code: string) => {
    if (!confirm(`Switch to "${code}"? The change takes effect at the end of the current billing period.`)) return;
    setBusy('change'); setError(null);
    try {
      const r = await platformBillingApi.changePlan(code);
      alert(r.no_change ? 'Already on this plan.' : 'Plan change scheduled for next period.');
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  };

  const cancel = async () => {
    if (!confirm('Cancel subscription at the end of the current period?')) return;
    setBusy('cancel'); setError(null);
    try { await platformBillingApi.cancel(); await load(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  };
  const resume = async () => {
    setBusy('resume'); setError(null);
    try { await platformBillingApi.resume(); await load(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  };

  const limitByKey = Object.fromEntries(limits.map((l) => [l.metric_key, l]));

  return (
    <div className="stack">
      <header>
        <h1>Billing</h1>
        <p className="muted">
          Your Litigate subscription, usage this period, and issued platform invoices.
          Payment runs through the same PayFast flow as chambers invoices.
        </p>
      </header>

      {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}

      {loading ? (
        <div className="card muted">Loading...</div>
      ) : (
        <>
          {/* Current plan card */}
          <div className="card stack">
            <div className="row">
              <div className="grow">
                <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current plan</div>
                <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>
                  {plan?.name || '(no plan)'} <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-text-muted)' }}>{plan?.code && `- ${plan.code}`}</span>
                </div>
                {plan?.description && <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{plan.description}</div>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {sub?.billing_cycle === 'annual' ? 'Annual' : 'Monthly'}
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>
                  {ZAR(sub?.billing_cycle === 'annual' ? plan?.price_annual_zar : plan?.price_monthly_zar)}
                </div>
              </div>
            </div>

            {sub && (
              <div className="row" style={{ gap: 24, fontSize: 13 }}>
                <div>
                  <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase' }}>Status</div>
                  <span className="pill">{humaniseStatus(sub.status)}</span>
                </div>
                <div>
                  <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase' }}>Period</div>
                  <div>{sub.current_period_start?.slice(0, 10)} - {sub.current_period_end?.slice(0, 10)}</div>
                </div>
                {sub.pending_plan_code && sub.pending_plan_code !== sub.plan_code && (
                  <div>
                    <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase' }}>Pending change</div>
                    <div>Moves to <b>{sub.pending_plan_code}</b> next period</div>
                  </div>
                )}
                {asBool(sub.cancel_at_period_end) && (
                  <div style={{ color: 'var(--color-danger)' }}>
                    Cancelling at period end
                  </div>
                )}
              </div>
            )}

            <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              {sub && asBool(sub.cancel_at_period_end) ? (
                <button onClick={resume} disabled={busy === 'resume'}>
                  {busy === 'resume' ? '...' : 'Undo cancellation'}
                </button>
              ) : sub ? (
                <button onClick={cancel} disabled={busy === 'cancel'}>
                  {busy === 'cancel' ? '...' : 'Cancel at period end'}
                </button>
              ) : null}
            </div>
          </div>

          {/* Usage meter */}
          <div className="card">
            <h3 style={{ margin: 0, marginBottom: 12 }}>Usage this period</h3>
            {usage.length === 0 ? (
              <div className="muted" style={{ fontSize: 13 }}>No activity recorded yet this period.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                {usage.map((u) => {
                  const limit = limitByKey[u.metric_key];
                  const pct = limit?.limit_value ? Math.min(100, Math.round((u.current_value / Number(limit.limit_value)) * 100)) : null;
                  const approachingLimit = pct != null && pct >= 80;
                  return (
                    <div key={u.metric_key} style={{ padding: 12, border: '1px solid var(--color-border)', borderRadius: 8 }}>
                      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {humanise(u.metric_key)}
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>
                        {u.current_value}
                        {limit?.limit_value && (
                          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-muted)' }}> / {limit.limit_value}</span>
                        )}
                      </div>
                      {pct != null && (
                        <div style={{ marginTop: 6, height: 4, background: 'var(--color-surface-3)', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: approachingLimit ? 'var(--color-danger)' : 'var(--color-accent)' }} />
                        </div>
                      )}
                      {u.peak_value > u.current_value && (
                        <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>peak: {u.peak_value}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Switch plan */}
          {plans.length > 0 && (
            <div className="card">
              <h3 style={{ margin: 0, marginBottom: 10 }}>Switch plan</h3>
              <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
                The swap takes effect at the end of the current billing period. No pro-rata refunds.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12, marginTop: 12 }}>
                {plans.map((p) => {
                  const isCurrent = p.code === sub?.plan_code;
                  const isPending = p.code === sub?.pending_plan_code;
                  return (
                    <div
                      key={p.code}
                      style={{
                        padding: 14,
                        border: `2px solid ${isCurrent ? 'var(--color-accent)' : 'var(--color-border)'}`,
                        background: isCurrent ? 'var(--color-accent-soft)' : 'var(--color-surface)',
                        borderRadius: 10,
                      }}
                    >
                      <div style={{ fontWeight: 700 }}>{p.name}</div>
                      <div className="muted" style={{ fontSize: 11, fontFamily: 'ui-monospace, monospace' }}>{p.code}</div>
                      <div style={{ fontSize: 20, fontWeight: 600, margin: '10px 0' }}>
                        {ZAR(p.price_monthly_zar)}<span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>/mo</span>
                      </div>
                      {p.description && <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>{p.description}</div>}
                      {isCurrent ? (
                        <span className="pill accent">Current plan</span>
                      ) : isPending ? (
                        <span className="pill">Scheduled</span>
                      ) : (
                        <button onClick={() => changePlan(p.code)} disabled={busy === 'change'}>Switch to this</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Invoice history */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 18px', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface-2)',
            }}>
              <h3 style={{ margin: 0 }}>Platform invoices</h3>
              <button onClick={load}>Refresh</button>
            </div>
            {invoices.length === 0 ? (
              <div className="muted" style={{ padding: 18 }}>
                No invoices yet. The first monthly invoice will issue at the end of your current period.
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Number</th>
                    <th>Period</th>
                    <th>Issued</th>
                    <th>Due</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th style={{ textAlign: 'right' }}>Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const outstanding = Number(inv.total_zar) - Number(inv.amount_paid_zar || 0);
                    return (
                      <tr key={String(inv.ROWID)}>
                        <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{inv.number}</td>
                        <td className="muted" style={{ fontSize: 12 }}>
                          {inv.period_start?.slice(0, 10)} - {inv.period_end?.slice(0, 10)}
                        </td>
                        <td>{inv.issued_at?.slice(0, 10)}</td>
                        <td>{inv.due_at?.slice(0, 10) || '-'}</td>
                        <td><span className={`pill ${inv.status === 'paid' ? 'success' : inv.status === 'overdue' ? 'danger' : ''}`}>{humaniseStatus(inv.status)}</span></td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{ZAR(inv.total_zar)}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: outstanding > 0 ? 'var(--color-danger)' : 'var(--color-text-muted)' }}>
                          {ZAR(outstanding)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
